import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import CaseFollowUpService from '../../../Api/services/case-follow-up.service';
import { useTranslation } from 'react-i18next';
import type { components } from '../../../Api/types/openapi';

type CaseStatusHistoryUSCISResponse = components['schemas']['CaseStatusHistoryUSCISResponse'];
type UpdateCaseHistoryLawyer = components['schemas']['UpdateCaseHistoryLawyer'];

interface AddNoteModalProps {
  isOpen: boolean;
  toggle: () => void;
  caseHistories: CaseStatusHistoryUSCISResponse[];
  onSuccess: () => void;
}

const AddNoteModal: React.FC<AddNoteModalProps> = ({
  isOpen,
  toggle,
  caseHistories,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<UpdateCaseHistoryLawyer>({
    case_history: 0,
    comment: '',
  });

  useEffect(() => {
    if (isOpen && caseHistories.length > 0) {
      // Seleccionar el primer historial por defecto
      setFormData({
        case_history: caseHistories[0].id || 0,
        comment: '',
      });
    }
  }, [isOpen, caseHistories]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'case_history' ? parseInt(value) : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.case_history || formData.case_history === 0) {
      toast.error(t('Seleccione un historial de caso'));
      return;
    }
    if (!formData.comment || formData.comment.trim().length < 10) {
      toast.error(t('El comentario debe tener al menos 10 caracteres'));
      return;
    }

    try {
      setLoading(true);
      await CaseFollowUpService.addLawyerNote({
        case_history: formData.case_history,
        comment: formData.comment.trim(),
      });
      toast.success(t('Nota agregada exitosamente'));
      setFormData({
        case_history: caseHistories[0]?.id || 0,
        comment: '',
      });
      toggle();
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al agregar nota');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        case_history: caseHistories[0]?.id || 0,
        comment: '',
      });
      toggle();
    }
  };

  if (caseHistories.length === 0) {
    return (
      <Modal isOpen={isOpen} toggle={handleClose}>
        <ModalHeader toggle={handleClose}>{t('Agregar Nota')}</ModalHeader>
        <ModalBody>
          <p>{t('No hay historiales de caso disponibles para agregar notas')}</p>
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={handleClose}>
            {t('Cerrar')}
          </Btn>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>{t('Agregar Nota')}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {caseHistories.length > 1 && (
            <FormGroup>
              <Label>{t('Seleccionar historial')}</Label>
              <Input
                type="select"
                name="case_history"
                value={formData.case_history}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {caseHistories.map((history) => (
                  <option key={history.id} value={history.id || 0}>
                    {history.created_at
                      ? `${new Date(history.created_at).toLocaleString('es-ES')}${history.comment ? ` - ${history.comment.substring(0, 50)}...` : ''}`
                      : `Historial #${history.id}`}
                  </option>
                ))}
              </Input>
            </FormGroup>
          )}

          <FormGroup>
            <Label>{t('Comentario')} *</Label>
            <Input
              type="textarea"
              name="comment"
              value={formData.comment || ''}
              onChange={handleChange}
              placeholder={t('Ingrese un comentario sobre el caso...')}
              rows={5}
              required
              disabled={loading}
            />
            <small className="text-muted">
              {t('Mínimo 10 caracteres')}
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={handleClose} disabled={loading}>
            {t('Cancelar')}
          </Btn>
          <Btn color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-1" />
                {t('Agregando...')}
              </>
            ) : (
              t('Agregar')
            )}
          </Btn>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddNoteModal;

